import React, { type ReactElement } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import RestrictedRoute from 'feature-components/routes/restricted-route';
import AdminGateway from 'pages/admin';
import AreaUsersGateway from 'pages/area-users';
import ClassesGateway from 'pages/classes';
import ClustersGateway from 'pages/clusters';
import { clustersPagePath } from 'pages/clusters/routes';
import DashboardPage from 'pages/dashboard';
import MarkingGateway from 'pages/marking';
import { markingPagePath } from 'pages/marking/routes';
import ReportsGateway from 'pages/reports';
import SubscriptionsGateway from 'pages/subscriptions';
import SystemWideReportsGateway from 'pages/system-wide-reports';
import TestInstancesGateway from 'pages/test-instances/components';
import UsersGateway from 'pages/users';
import { AuthorisableRoute } from 'policies/route.policy';

/**
 * Converts a path to a slug by removing the preceeding forward slash.
 */
function pathToSlug(path: string): string {
  return path.substring('/'.length);
}

export default function AuthorisedRoutes(): ReactElement {
  return (
    <Routes>
      <Route
        element={
          <RestrictedRoute route={AuthorisableRoute.Dashboard} redirectPath="/organisations" />
        }
      >
        <Route index element={<DashboardPage />} />
      </Route>
      <Route element={<RestrictedRoute route={AuthorisableRoute.AreaUsersWildcard} />}>
        <Route path="organisation-area-users/*" element={<AreaUsersGateway />} />
      </Route>
      <Route element={<RestrictedRoute route={AuthorisableRoute.DashboardSubpages} />}>
        <Route path="product/*" element={<TestInstancesGateway />} />
      </Route>
      <Route element={<RestrictedRoute route={AuthorisableRoute.ReportsWildcard} />}>
        <Route path="reports/*" element={<ReportsGateway />} />
      </Route>
      <Route element={<RestrictedRoute route={AuthorisableRoute.SystemWideReportsWildcard} />}>
        <Route path="system-wide-reports/*" element={<SystemWideReportsGateway />} />
      </Route>
      <Route element={<RestrictedRoute route={AuthorisableRoute.MarkingWildcard} />}>
        <Route path={`${pathToSlug(markingPagePath)}/*`} element={<MarkingGateway />} />
      </Route>
      <Route element={<RestrictedRoute route={AuthorisableRoute.ClustersWildcard} />}>
        <Route path={`${pathToSlug(clustersPagePath)}/*`} element={<ClustersGateway />} />
      </Route>
      <Route element={<RestrictedRoute route={AuthorisableRoute.AdminWildcard} />}>
        <Route path="admin/*" element={<AdminGateway />} />
      </Route>
      <Route element={<RestrictedRoute route={AuthorisableRoute.ClassesWildcard} />}>
        <Route path="classes/*" element={<ClassesGateway />} />
      </Route>
      <Route element={<RestrictedRoute route={AuthorisableRoute.UsersWildcard} />}>
        <Route path="organisation-users/*" element={<UsersGateway />} />
      </Route>
      <Route element={<RestrictedRoute route={AuthorisableRoute.SubscriptionsWildcard} />}>
        <Route path="subscriptions/*" element={<SubscriptionsGateway />} />
      </Route>

      {/* Redirect some urls that we have changed */}
      {/* subscriptions used to be tools but was changed after release */}
      <Route path="tools/*" element={<Navigate to="/subscriptions" replace />} />

      {/* If route not matched redirect to base route */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
